interface DeepSeekTranslationRequest {
  sourceLanguage: string
  targetLanguage: string
  text: {
    name: string
    description: string
    slug?: string
  }
  temperature?: number
  useJsonMode?: boolean
  type?: 'category' | 'challenge' // Add type to distinguish between category and challenge translations
}

interface DeepSeekTranslationResponse {
  name: string
  description: string
  slug: string
}

interface TranslationConfig {
  apiKey?: string
  apiUrl?: string
  temperature: number
  useJsonMode: boolean
}

class TranslationService {
  private config: TranslationConfig = {
    apiUrl: 'https://api.deepseek.com/v1/chat/completions',
    temperature: 1.3,
    useJsonMode: true
  }

  constructor() {
    // Get API key from environment variables (Vite client-side)
    this.config.apiKey = import.meta.env.VITE_DEEPSEEK_API_KEY
    
    // Debug logging
    if (import.meta.env.DEV) {
      console.log('🔍 TranslationService constructor:')
      console.log('  - VITE_DEEPSEEK_API_KEY:', import.meta.env.VITE_DEEPSEEK_API_KEY ? '✅ Found' : '❌ Not found')
      console.log('  - API Key (masked):', this.config.apiKey ? `${this.config.apiKey.substring(0, 8)}...${this.config.apiKey.substring(this.config.apiKey.length - 4)}` : '❌ Not set')
      console.log('  - API Key length:', this.config.apiKey?.length || 0)
      console.log('  - Will use:', this.config.apiKey ? 'DeepSeek API' : 'Fallback translations')
    }
  }

  setConfig(config: Partial<TranslationConfig>) {
    this.config = { ...this.config, ...config }
  }

  private getLanguageName(code: string): string {
    const languages: Record<string, string> = {
      en: 'English',
      fr: 'French',
      es: 'Spanish',
      de: 'German',
      it: 'Italian',
      pt: 'Portuguese'
    }
    return languages[code] || code
  }

  private createTranslationPrompt(request: DeepSeekTranslationRequest): string {
    const sourceLang = this.getLanguageName(request.sourceLanguage)
    const targetLang = this.getLanguageName(request.targetLanguage)
    const isChallenge = request.type === 'challenge'

    if (isChallenge) {
      return `You are a professional translator specialized in challenge challenge translations for a habit-building app.

Task: Translate the following challenge challenge information from ${sourceLang} to ${targetLang}.

Source (${sourceLang}):
- Name: "${request.text.name}"
- Description: "${request.text.description}"

Requirements:
1. Provide only the direct translation without any additional context, explanations, or language indicators
2. Maintain the meaning and context for habit-building challenge
3. Keep the tone motivational, inspiring, and actionable
4. Ensure cultural appropriateness for ${targetLang} speakers
5. The name should be clear, specific, and actionable (e.g., "Drink 8 glasses of water daily")
6. The description should motivate users and explain the benefits of this habit
7. Focus on personal growth and positive lifestyle changes
8. Use imperative or encouraging language where appropriate
9. Do not add phrases like "(translated to ${targetLang})" or "(${targetLang})" to the output

${request.useJsonMode ? `
Please respond in JSON format with clean translations only:
{
  "name": "translated challenge name",
  "description": "translated challenge description"
}` : `
Please provide the clean translation in the following format:
Name: [translated name]
Description: [translated description]`}`
    }

    return `You are a professional translator specialized in challenge category translations for a habit-building app.

Task: Translate the following category information from ${sourceLang} to ${targetLang}.

Source (${sourceLang}):
- Name: "${request.text.name}"
- Description: "${request.text.description}"${request.text.slug ? `\n- Slug: "${request.text.slug}"` : ''}

Requirements:
1. Provide only the direct translation without any additional context, explanations, or language indicators
2. Maintain the meaning and context for habit-building challenges
3. Keep the tone motivational and clear
4. Ensure cultural appropriateness for ${targetLang} speakers
5. The name should be concise and memorable
6. The description should be engaging and descriptive${request.text.slug ? `\n7. The slug should be a URL-friendly version of the translated name (lowercase, hyphens instead of spaces, no special characters)` : ''}
${request.text.slug ? '8' : '7'}. Do not add phrases like "(translated to ${targetLang})" or "(${targetLang})" to the output

${request.useJsonMode ? `
Please respond in JSON format with clean translations only:
{
  "name": "translated category name",
  "description": "translated category description"${request.text.slug ? ',\n  "slug": "translated-url-friendly-slug"' : ''}
}` : `
Please provide the clean translation in the following format:
Name: [translated name]
Description: [translated description]${request.text.slug ? '\nSlug: [translated-slug]' : ''}`}`
  }

  async translateCategory(request: DeepSeekTranslationRequest): Promise<DeepSeekTranslationResponse> {
    if (!this.config.apiKey) {
      console.warn(`⚠️ DeepSeek API key not found, using fallback translation for ${request.text.name} -> ${request.targetLanguage}`)
      return this.fallbackTranslation(request)
    }

    if (import.meta.env.DEV) {
      console.log(`🚀 Using DeepSeek API for translation: ${request.text.name} -> ${request.targetLanguage}`)
      console.log(`🔑 API Key available: ${this.config.apiKey?.length} characters`)
    }

    try {
      const prompt = this.createTranslationPrompt(request)
      
      const requestBody = {
        model: 'deepseek-chat', // Changed from 'deepseek-reasoner' to standard chat model
        messages: [
          {
            role: 'user',
            content: prompt
          }
        ],
        temperature: this.config.temperature,
        max_tokens: 1000,
        response_format: request.useJsonMode ? { type: 'json_object' } : undefined
      }
      
      // Enhanced logging for debugging
      if (import.meta.env.DEV) {
        console.log('🚀 DeepSeek API Request:', {
          url: this.config.apiUrl,
          model: requestBody.model,
          temperature: requestBody.temperature,
          max_tokens: requestBody.max_tokens,
          useJsonMode: request.useJsonMode,
          promptLength: prompt.length,
          hasApiKey: !!this.config.apiKey
        })
      }
      
      const response = await fetch(this.config.apiUrl!, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.config.apiKey}`
        },
        body: JSON.stringify(requestBody)
      })

      if (!response.ok) {
        const errorText = await response.text()
        console.error('❌ DeepSeek API HTTP Error:', {
          status: response.status,
          statusText: response.statusText,
          headers: Object.fromEntries(response.headers.entries()),
          body: errorText,
          url: this.config.apiUrl,
          hasApiKey: !!this.config.apiKey,
          apiKeyLength: this.config.apiKey?.length
        })
        throw new Error(`DeepSeek API error: ${response.status} ${response.statusText} - ${errorText}`)
      }

      const data = await response.json()
      
      // Enhanced logging for debugging
      if (import.meta.env.DEV) {
        console.log('🔍 DeepSeek API Response:', {
          status: response.status,
          data: data,
          choices: data.choices,
          firstChoice: data.choices?.[0],
          message: data.choices?.[0]?.message,
          content: data.choices?.[0]?.message?.content
        })
      }
      
      const content = data.choices?.[0]?.message?.content

      if (!content) {
        console.error('❌ DeepSeek API returned empty content:', {
          fullResponse: data,
          choices: data.choices,
          choicesLength: data.choices?.length,
          firstChoice: data.choices?.[0],
          hasMessage: !!data.choices?.[0]?.message,
          contentValue: data.choices?.[0]?.message?.content
        })
        throw new Error('No content received from DeepSeek API')
      }

      // Helper function to generate slug from text
      const generateSlug = (text: string): string => {
        return text
          .toLowerCase()
          .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
          .replace(/\s+/g, '-') // Replace spaces with hyphens
          .replace(/-+/g, '-') // Replace multiple hyphens with single
          .trim() // Remove leading/trailing spaces
          .replace(/^-+|-+$/g, '') // Remove leading/trailing hyphens
      }

      if (request.useJsonMode) {
        try {
          const parsed = JSON.parse(content)
          // Ensure slug is always present
          if (!parsed.slug && parsed.name) {
            parsed.slug = generateSlug(parsed.name)
          }
          
          // Enhanced logging for debugging language mapping
          if (import.meta.env.DEV) {
            console.log(`🔍 Translation result for ${request.sourceLanguage} -> ${request.targetLanguage}:`, {
              originalName: request.text.name,
              translatedName: parsed.name,
              targetLanguage: request.targetLanguage,
              targetLanguageName: this.getLanguageName(request.targetLanguage)
            })
          }
          
          return parsed
        } catch (parseError) {
          console.error('Failed to parse JSON response:', content)
          throw new Error('Invalid JSON response from DeepSeek API')
        }
      } else {
        // Parse non-JSON response
        const nameMatch = content.match(/Name:\s*(.+)/i)
        const descMatch = content.match(/Description:\s*(.+)/i)
        
        if (!nameMatch || !descMatch) {
          throw new Error('Failed to parse translation response')
        }

        const name = nameMatch[1].trim()
        return {
          name: name,
          description: descMatch[1].trim(),
          slug: generateSlug(name)
        }
      }
    } catch (error) {
      console.error(`❌ DeepSeek API error for ${request.text.name} -> ${request.targetLanguage}:`, error)
      
      // If API fails, use fallback
      console.warn(`⚠️ Using fallback translation for ${request.text.name} -> ${request.targetLanguage}`)
      return this.fallbackTranslation(request)
    }
  }

  private fallbackTranslation(request: DeepSeekTranslationRequest): DeepSeekTranslationResponse {
    // Helper function to generate slug from text
    const generateSlug = (text: string): string => {
      return text
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
        .replace(/\s+/g, '-') // Replace spaces with hyphens
        .replace(/-+/g, '-') // Replace multiple hyphens with single
        .trim() // Remove leading/trailing spaces
        .replace(/^-+|-+$/g, '') // Remove leading/trailing hyphens
    }

    // Create language-specific fallback by adding language indicator
    const targetLanguageName = this.getLanguageName(request.targetLanguage)
    
    // If it's the same language, just return the original
    if (request.sourceLanguage === request.targetLanguage) {
      return {
        name: request.text.name,
        description: request.text.description,
        slug: request.text.slug || generateSlug(request.text.name)
      }
    }
    
    // For different languages, create better fallback that's more obvious it needs translation
    const response: DeepSeekTranslationResponse = {
      name: `[${targetLanguageName}] ${request.text.name}`,
      description: `[${targetLanguageName}] ${request.text.description}`,
      slug: request.text.slug || generateSlug(request.text.name + `-${request.targetLanguage}`)
    }
    
    return response
  }

  async translateBatch(requests: DeepSeekTranslationRequest[], maxConcurrency: number = 3): Promise<DeepSeekTranslationResponse[]> {
    // Process translations in parallel batches to respect rate limits while being efficient
    const results: (DeepSeekTranslationResponse | null)[] = new Array(requests.length).fill(null)
    
    // Create batches for parallel processing
    const batches: DeepSeekTranslationRequest[][] = []
    for (let i = 0; i < requests.length; i += maxConcurrency) {
      batches.push(requests.slice(i, i + maxConcurrency))
    }
    
    // Process each batch in parallel
    for (const batch of batches) {
      const batchPromises = batch.map(async (request, batchIndex) => {
        const originalIndex = batches.indexOf(batch) * maxConcurrency + batchIndex
        
        try {
          const result = await this.translateCategory(request)
          results[originalIndex] = result
          return { index: originalIndex, result, error: null }
        } catch (error) {
          console.error(`Failed to translate ${request.targetLanguage}:`, error)
          const fallback = this.fallbackTranslation(request)
          results[originalIndex] = fallback
          return { index: originalIndex, result: fallback, error }
        }
      })
      
      // Wait for the current batch to complete before starting the next
      await Promise.all(batchPromises)
      
      // Small delay between batches to respect rate limits
      if (batches.indexOf(batch) < batches.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 200))
      }
    }
    
    return results.filter(result => result !== null) as DeepSeekTranslationResponse[]
  }

  async translateParallel(
    requests: DeepSeekTranslationRequest[], 
    onProgress?: (completed: number, total: number, result?: DeepSeekTranslationResponse, error?: any, requestIndex?: number) => void,
    maxConcurrency: number = 3
  ): Promise<DeepSeekTranslationResponse[]> {
    const results: (DeepSeekTranslationResponse | null)[] = new Array(requests.length).fill(null)
    let completed = 0
    
    // Simple parallel processing with Promise.all and chunking
    const chunks: DeepSeekTranslationRequest[][] = []
    for (let i = 0; i < requests.length; i += maxConcurrency) {
      chunks.push(requests.slice(i, i + maxConcurrency))
    }
    
    // Process chunks sequentially, but items within each chunk in parallel
    for (const chunk of chunks) {
      const chunkPromises = chunk.map(async (request, chunkIndex) => {
        const originalIndex = chunks.indexOf(chunk) * maxConcurrency + chunkIndex
        
        try {
          const result = await this.translateCategory(request)
          results[originalIndex] = result
          completed++
          onProgress?.(completed, requests.length, result, undefined, originalIndex)
          return result
        } catch (error) {
          console.error(`Failed to translate ${request.targetLanguage}:`, error)
          const fallback = this.fallbackTranslation(request)
          results[originalIndex] = fallback
          completed++
          onProgress?.(completed, requests.length, fallback, error, originalIndex)
          return fallback
        }
      })
      
      // Wait for current chunk to complete before starting next
      await Promise.all(chunkPromises)
      
      // Small delay between chunks if not the last one
      if (chunks.indexOf(chunk) < chunks.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 100))
      }
    }
    
    return results.filter(result => result !== null) as DeepSeekTranslationResponse[]
  }

  // Challenge-specific translation methods
  async translateChallenge(request: Omit<DeepSeekTranslationRequest, 'type'>): Promise<DeepSeekTranslationResponse> {
    return this.translateCategory({ ...request, type: 'challenge' })
  }

  async translateChallengeParallel(
    requests: Omit<DeepSeekTranslationRequest, 'type'>[], 
    onProgress?: (completed: number, total: number, result?: DeepSeekTranslationResponse, error?: any, requestIndex?: number) => void,
    maxConcurrency: number = 3
  ): Promise<DeepSeekTranslationResponse[]> {
    const challengeRequests = requests.map(req => ({ ...req, type: 'challenge' as const }))
    return this.translateParallel(challengeRequests, onProgress, maxConcurrency)
  }

  async translateChallengeBatch(requests: Omit<DeepSeekTranslationRequest, 'type'>[], maxConcurrency: number = 3): Promise<DeepSeekTranslationResponse[]> {
    const challengeRequests = requests.map(req => ({ ...req, type: 'challenge' as const }))
    return this.translateBatch(challengeRequests, maxConcurrency)
  }
}

export const translationService = new TranslationService()
export type { DeepSeekTranslationRequest, DeepSeekTranslationResponse, TranslationConfig } 